import { create } from 'zustand';

import { type IAuthenticatedUser } from '@workinance/shared/interfaces/auth.interface';
import {
	type IBusiness,
	type IInventorySettings,
	type IPosSettings,
	type IReceiptSettings,
	type ISaleSettings,
} from '@workinance/shared/interfaces/business.interface';
import { useAlertStore } from '@workinance/shared/stores/alert.store';
import {
	applyPreferredTheme,
	useThemeStore,
	type ThemeName,
} from '@workinance/shared/stores/theme.store';

import {
	clearAuthData,
	getAuthData,
	saveAuthData,
	type IAuthData,
} from '@/lib/auth-storage';
import { sendHttpRequest } from '@/lib/http';

interface IAuthStore {
	user: IAuthenticatedUser | null;
	token: string | null;
	/** Renews `token` when it expires. Saved in the same record as the token. */
	refreshToken: string | null;
	businesses: IBusiness[];
	activeBusinessId: string | null;
	isAuthenticated: boolean;
	isLoadingBusinesses: boolean;

	login: (authData: {
		token: string;
		refreshToken: string;
		user: IAuthenticatedUser;
		/** Every business the owner has. Preferred over `business` when present. */
		businesses?: IBusiness[];
		business?: IBusiness | null;
	}) => void;
	loginWithCredentials: (payload: {
		email: string;
		password: string;
	}) => Promise<boolean>;
	register: (payload: {
		firstName: string;
		surName: string;
		email: string;
		password: string;
	}) => Promise<boolean>;
	logout: () => void;
	fetchBusinesses: () => Promise<void>;
	createBusiness: (payload: {
		name: string;
		address?: string;
		/** ISO 3166 two-letter code. The server gives the business that country's currency. */
		countryCode: string;
	}) => Promise<boolean>;
	setActiveBusinessId: (id: string) => Promise<void>;
	/** Swaps in a fresh access token after the refresh token renewed it, and saves it with the session. */
	setAccessToken: (token: string) => Promise<void>;
	/** Applies a theme right away and saves it to the user's account. */
	updatePreferredTheme: (theme: ThemeName) => Promise<void>;
	/** Applies the notification choices right away and saves them to the user's account. */
	updateNotificationSettings: (changes: {
		acceptEmailNotification?: boolean;
		acceptSmsNotification?: boolean;
	}) => Promise<void>;
	updateBusiness: (
		businessId: string,
		updates: {
			name: string;
			address?: string;
			phoneNumber?: string;
			email?: string;
			taxId?: string;
			staffRules?: string;
		},
	) => Promise<boolean>;
	/** Saves country, currency, inventory and sales settings; anything left out keeps its value. */
	updateBusinessSettings: (
		businessId: string,
		settings: {
			/** ISO 3166 two-letter code. */
			countryCode?: string;
			/** ISO 4217 code. */
			currency?: string;
			clockFormat?: string;
			dateFormat?: string;
			timeZone?: string;
			workingHours?: Record<
				string,
				{ isOpen?: boolean; opensAt?: string; closesAt?: string }
			>;
			inventory?: Partial<IInventorySettings>;
			sales?: Partial<ISaleSettings>;
			pos?: Partial<IPosSettings>;
			receipt?: Partial<IReceiptSettings>;
		},
	) => Promise<boolean>;
	setEmailVerified: () => void;
	verifyEmail: (verificationCode: string) => Promise<boolean>;
	resendVerification: () => Promise<boolean>;
	requestPasswordReset: (email: string) => Promise<boolean>;
	resetPassword: (payload: {
		email: string;
		verificationCode: string;
		password: string;
	}) => Promise<boolean>;
	/** Who the user is comes from the device's stored session on every start. Nothing renders until this resolves. */
	restoreAuthentication: () => Promise<void>;
}

export const useAuthStore = create<IAuthStore>((set, get) => ({
	user: null,
	token: null,
	refreshToken: null,
	businesses: [],
	activeBusinessId: null,
	isAuthenticated: false,
	isLoadingBusinesses: false,

	login: ({
		token,
		refreshToken,
		user,
		business,
		businesses: businessList,
	}) => {
		const businesses = businessList ?? (business ? [business] : []);
		const activeBusinessId = businesses[0]?.id ?? null;
		void saveAuthData<IAuthData>({
			token,
			refreshToken,
			user,
			businesses,
			activeBusinessId,
		}).catch(() => {});
		set({
			user,
			token,
			refreshToken,
			businesses,
			activeBusinessId,
			isAuthenticated: true,
		});
		applyPreferredTheme(user.preferredTheme);
	},

	loginWithCredentials: async ({ email, password }) => {
		try {
			const { data: response } = await sendHttpRequest<{
				token: string;
				refreshToken: string;
				user: IAuthenticatedUser;
				businesses?: IBusiness[];
				business?: IBusiness | null;
			}>({
				url: 'auth/login',
				method: 'post',
				// No businessCode: the server treats this as an owner login by email.
				data: { username: email, password },
			});

			console.log('loginWithCredentials response:', response);

			if (response.token) {
				get().login({
					token: response.token,
					refreshToken: response.refreshToken,
					user: response.user,
					businesses: response.businesses,
					business: response.business,
				});
			}
			return true;
		} catch (error: any) {
			useAlertStore
				.getState()
				.showError(error, 'Login failed. Please try again.');
			return false;
		}
	},

	register: async ({ firstName, surName, email, password }) => {
		try {
			const { data: response } = await sendHttpRequest<{
				token: string;
				refreshToken: string;
				user: IAuthenticatedUser;
			}>({
				url: 'auth/register',
				method: 'post',
				data: { firstName, surName, email, password },
			});

			if (response.token) {
				get().login({
					token: response.token,
					refreshToken: response.refreshToken,
					user: response.user,
				});
			}
			return true;
		} catch (error: any) {
			useAlertStore
				.getState()
				.showError(error, 'Registration failed. Please try again.');
			return false;
		}
	},

	logout: () => {
		// Tell the server so the refresh token stops working everywhere, not just on this device. If the
		// request fails the device is still signed out locally.
		const { refreshToken } = get();
		if (refreshToken) {
			void sendHttpRequest({
				url: 'auth/logout',
				method: 'post',
				data: { refreshToken },
			}).catch(() => {});
		}
		void clearAuthData().catch(() => {});
		set({
			user: null,
			token: null,
			refreshToken: null,
			businesses: [],
			activeBusinessId: null,
			isAuthenticated: false,
		});
	},

	/** Pulls the owner's full business list from the server and syncs it into the store + persisted session. */
	fetchBusinesses: async () => {
		set({ isLoadingBusinesses: true });
		try {
			const { data: response } = await sendHttpRequest<{
				businesses: IBusiness[];
			}>({
				url: 'businesses',
				method: 'get',
			});
			const businesses = response.businesses ?? [];
			const { activeBusinessId } = get();
			const nextActiveId = businesses.some(
				(business) => business.id === activeBusinessId,
			)
				? activeBusinessId
				: (businesses[0]?.id ?? null);

			set({ businesses, activeBusinessId: nextActiveId });
			const { token, user } = get();
			if (token && user) {
				void saveAuthData<IAuthData>({
					token,
					user,
					businesses,
					activeBusinessId: nextActiveId,
				}).catch(() => {});
			}
		} catch {
			// Keep whatever the store already has rather than clearing it on a failed refresh.
		} finally {
			set({ isLoadingBusinesses: false });
		}
	},

	/** Creates the owner's first (or next) business and makes it the active one. */
	createBusiness: async (payload) => {
		try {
			const { data: response } = await sendHttpRequest<{ business: IBusiness }>(
				{
					url: 'businesses',
					method: 'post',
					data: payload,
				},
			);

			const { businesses, token, user } = get();
			const nextBusinesses = [...businesses, response.business];
			set({
				businesses: nextBusinesses,
				activeBusinessId: response.business.id,
			});
			if (token && user) {
				void saveAuthData<IAuthData>({
					token,
					user,
					businesses: nextBusinesses,
					activeBusinessId: response.business.id,
				}).catch(() => {});
			}
			return true;
		} catch (error: any) {
			useAlertStore
				.getState()
				.showError(error, 'Could not create your business. Please try again.');
			return false;
		}
	},

	setActiveBusinessId: async (id) => {
		set({ activeBusinessId: id });
		const { token, user, businesses } = get();
		if (token && user) {
			// Awaited (not fire-and-forget) so a caller that reloads the page right after this
			// resolves can be sure the new activeBusinessId is actually on disk before the reload
			// reads it back -- otherwise a reload could land before the IndexedDB write flushes
			// and restore the previous business.
			try {
				await saveAuthData<IAuthData>({
					token,
					user,
					businesses,
					activeBusinessId: id,
				});
			} catch {
				// Browser storage can be unavailable (private mode, blocked IndexedDB); the in-memory
				// state above still switched, it just won't survive a reload.
			}
		}
	},

	setAccessToken: async (token) => {
		const { user, businesses, activeBusinessId } = get();
		set({ token });
		if (user) {
			await saveAuthData<IAuthData>({
				token,
				user,
				businesses,
				activeBusinessId,
			}).catch(() => {});
		}
	},

	updatePreferredTheme: async (theme) => {
		const { user, token, businesses, activeBusinessId } = get();
		if (!user || !token) return;

		// The theme only switches once the server has saved it, so what is on screen always matches the account.
		try {
			await sendHttpRequest({
				url: 'users/me/settings',
				method: 'patch',
				data: { preferredTheme: theme },
			});

			useThemeStore.getState().setTheme(theme);
			const updatedUser = { ...user, preferredTheme: theme };
			set({ user: updatedUser });
			void saveAuthData<IAuthData>({
				token,
				user: updatedUser,
				businesses,
				activeBusinessId,
			}).catch(() => {});
		} catch (error: any) {
			useAlertStore
				.getState()
				.showError(error, 'Could not save your theme. Please try again.');
		}
	},

	updateNotificationSettings: async (changes) => {
		const { user, token, businesses, activeBusinessId } = get();
		if (!user || !token) return;

		// Show the new choice straight away; the save below makes it stick on the account.
		const previousUser = user;
		const updatedUser = {
			...user,
			settings: { ...user.settings, ...changes },
		};
		set({ user: updatedUser });
		void saveAuthData<IAuthData>({
			token,
			user: updatedUser,
			businesses,
			activeBusinessId,
		}).catch(() => {});

		try {
			await sendHttpRequest({
				url: 'users/me/settings',
				method: 'patch',
				data: changes,
			});
			useAlertStore.getState().showSuccess('Notification settings saved.');
		} catch (error: any) {
			set({ user: previousUser });
			void saveAuthData<IAuthData>({
				token,
				user: previousUser,
				businesses,
				activeBusinessId,
			}).catch(() => {});
			useAlertStore
				.getState()
				.showError(
					error,
					'Could not save your notification settings. Please try again.',
				);
		}
	},

	/** Saves one business's details to the server, then syncs the result into the store. Not limited to the active business -- any business the owner has can be edited this way. */
	updateBusiness: async (businessId, updates) => {
		const { businesses, activeBusinessId, token, user } = get();
		const business = businesses.find((item) => item.id === businessId);
		if (!business) return false;

		try {
			const { data: response } = await sendHttpRequest<{
				business?: IBusiness;
			}>({
				url: `businesses/${businessId}`,
				method: 'patch',
				data: updates,
			});

			// The server sends the business as saved; without it, fall back to the details sent.
			const updatedBusiness = response?.business ?? { ...business, ...updates };
			const nextBusinesses = businesses.map((item) =>
				item.id === businessId ? updatedBusiness : item,
			);
			set({ businesses: nextBusinesses });
			if (token && user) {
				void saveAuthData<IAuthData>({
					token,
					user,
					businesses: nextBusinesses,
					activeBusinessId,
				}).catch(() => {});
			}
			useAlertStore.getState().showSuccess('Business details updated.');
			return true;
		} catch (error: any) {
			useAlertStore
				.getState()
				.showError(error, 'Could not update your business. Please try again.');
			return false;
		}
	},

	updateBusinessSettings: async (businessId, settings) => {
		const { businesses, activeBusinessId, token, user } = get();
		if (!businesses.some((item) => item.id === businessId)) return false;

		try {
			const { data: response } = await sendHttpRequest<{
				business?: IBusiness;
			}>({
				url: `businesses/${businessId}/settings`,
				method: 'patch',
				data: settings,
			});
			// The server merges the settings with what was saved, so only its copy is exact.
			const savedBusiness = response?.business;
			if (savedBusiness) {
				const nextBusinesses = businesses.map((item) =>
					item.id === businessId ? savedBusiness : item,
				);
				set({ businesses: nextBusinesses });
				if (token && user) {
					void saveAuthData<IAuthData>({
						token,
						user,
						businesses: nextBusinesses,
						activeBusinessId,
					}).catch(() => {});
				}
			}
			useAlertStore.getState().showSuccess('Business settings updated.');
			return true;
		} catch (error: any) {
			useAlertStore
				.getState()
				.showError(error, 'Could not update the settings. Please try again.');
			return false;
		}
	},

	setEmailVerified: () => {
		const { token, user, businesses, activeBusinessId } = get();
		if (!token || !user) return;
		const verifiedUser = { ...user, emailIsVerified: true };
		set({ user: verifiedUser });
		void saveAuthData<IAuthData>({
			token,
			user: verifiedUser,
			businesses,
			activeBusinessId,
		}).catch(() => {});
	},

	verifyEmail: async (verificationCode) => {
		try {
			await sendHttpRequest({
				url: 'auth/verification',
				method: 'patch',
				data: { verificationCode },
			});
			get().setEmailVerified();
			return true;
		} catch (error: any) {
			useAlertStore
				.getState()
				.showError(error, 'Could not verify your email. Please try again.');
			return false;
		}
	},

	resendVerification: async () => {
		try {
			await sendHttpRequest({
				url: 'auth/verification/resend',
				method: 'post',
			});
			useAlertStore.getState().showSuccess('A new code has been sent.');
			return true;
		} catch (error: any) {
			useAlertStore
				.getState()
				.showError(error, 'Could not resend the code. Please try again.');
			return false;
		}
	},

	requestPasswordReset: async (email) => {
		try {
			await sendHttpRequest({
				url: 'auth/forgot-password',
				method: 'post',
				data: { email },
			});
			useAlertStore
				.getState()
				.showSuccess('Check your email for a reset code.');
			return true;
		} catch (error: any) {
			useAlertStore.getState().showError(error, 'Could not send reset code.');
			return false;
		}
	},

	resetPassword: async ({ email, verificationCode, password }) => {
		try {
			await sendHttpRequest({
				url: 'auth/reset-password',
				method: 'post',
				data: { email, verificationCode, password },
			});
			return true;
		} catch (error: any) {
			useAlertStore.getState().showError(error, 'Could not reset password.');
			return false;
		}
	},

	async restoreAuthentication() {
		let authData: IAuthData | null = null;
		try {
			authData = await getAuthData<IAuthData>();
		} catch {
			// Corrupted or inaccessible browser storage (private mode, blocked IndexedDB, stale schema) — treat as signed out.
		}

		if (!authData?.token) {
			set({
				user: null,
				token: null,
				refreshToken: null,
				businesses: [],
				activeBusinessId: null,
				isAuthenticated: false,
			});
			return;
		}

		// Set the tokens first so the request interceptor can attach the token, and renew it with the
		// refresh token if it has expired, for the business/get call below.
		const refreshToken = authData.refreshToken ?? null;
		set({ user: authData.user, token: authData.token, refreshToken });
		applyPreferredTheme(authData.user.preferredTheme);

		let businesses = authData.businesses ?? [];

		try {
			const { data: response } = await sendHttpRequest<{
				businesses: IBusiness[];
			}>({
				url: 'businesses',
				method: 'get',
			});
			businesses = response.businesses ?? businesses;
		} catch {
			// Keep the cached list from the last session rather than clearing it on a network hiccup.
		}

		// A dead refresh token during the fetch above already signs the session out (the request
		// interceptor's logout(), triggered by a 401 it could not recover from) -- respect that
		// instead of reviving it below with stale cached data.
		if (!get().token) return;

		const activeBusinessId =
			authData.activeBusinessId &&
			businesses.some((business) => business.id === authData.activeBusinessId)
				? authData.activeBusinessId
				: (businesses[0]?.id ?? null);

		set({
			user: authData.user,
			token: authData.token,
			businesses,
			activeBusinessId,
			isAuthenticated: true,
		});
	},
}));

/** The business every page should act on: the one the owner explicitly picked, or the first they have. */
export function useActiveBusiness(): IBusiness | null {
	return useAuthStore(
		(state) =>
			state.businesses.find(
				(business) => business.id === state.activeBusinessId,
			) ??
			state.businesses[0] ??
			null,
	);
}
